import axios from 'axios';
import { noteGrowthFreeMode } from './lib/growthMode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Debug logging helper
const DEBUG = false; // Set to true only for local auth debugging
const debugLog = (message, data = null) => {
  if (DEBUG) {
    console.log(`[AUTH DEBUG] ${message}`, data || '');
  }
};

// Helper to get session token from localStorage
const getSessionToken = () => {
  const token = localStorage.getItem('session_token');
  debugLog('getSessionToken called', { 
    hasToken: !!token, 
    tokenPrefix: token ? token.substring(0, 20) + '...' : null 
  });
  return token;
};

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include Authorization header
api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    debugLog('Request interceptor added Authorization header', {
      url: config.url,
      method: config.method,
      hasAuthHeader: true
    });
  } else {
    debugLog('Request interceptor - NO token available', {
      url: config.url,
      method: config.method
    });
  }
  return config;
});

// Add response interceptor to log auth errors and handle global 401
api.interceptors.response.use(
  (response) => {
    debugLog('Response received', {
      url: response.config.url,
      status: response.status
    });
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      debugLog('401 Unauthorized error', {
        url: error.config?.url,
        responseData: error.response?.data,
      });
      // Clear stale token and redirect to login (skip if already on login page)
      if (!window.location.pathname.includes('/login')) {
        localStorage.removeItem('session_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Helper to store session token
export const setSessionToken = (token) => {
  debugLog('setSessionToken called', { hasToken: !!token });
  if (token) {
    localStorage.setItem('session_token', token);
    debugLog('Token stored in localStorage', { tokenPrefix: token.substring(0, 20) + '...' });
  } else {
    localStorage.removeItem('session_token');
    debugLog('Token removed from localStorage');
  }
};

export const authAPI = {
  exchangeSession: async (sessionId, firstTouch = null) => {
    debugLog('exchangeSession called', { sessionIdPrefix: sessionId?.substring(0, 20) });
    const payload = { session_id: sessionId };
    if (firstTouch) payload.first_touch = firstTouch; // signup-source attribution (new users only)
    const response = await api.post('/auth/session', payload);
    debugLog('exchangeSession response', { 
      hasSessionToken: !!response.data?.session_token,
      userData: response.data?.email 
    });
    // Store token in localStorage for cross-origin fallback
    if (response.data?.session_token) {
      setSessionToken(response.data.session_token);
    }
    return response;
  },
  getMe: () => {
    debugLog('getMe called');
    return api.get('/auth/me');
  },
  updateMe: (data) => api.put('/auth/me', data),
  logout: async () => {
    debugLog('logout called');
    const response = await api.post('/auth/logout');
    setSessionToken(null);
    return response;
  },
};

export const configAPI = {
  get: () => api.get('/config'),
};

export const questionsAPI = {
  getAll: (params) => api.get('/questions', { params }),
  getById: (id) => api.get(`/questions/${id}`),
  getRandom: (params) => api.get('/questions/random', { params }),
  getRecommended: (experience) => api.get('/questions/recommended', { params: { experience } }),
  suggest: (data) => api.post('/questions/suggest', data),
  getMySuggestions: () => api.get('/questions/my-suggestions'),
};

// Public Resources / Articles hub — no auth required (the interceptor simply
// omits the Authorization header when no token is present).
export const articlesAPI = {
  list: (category) => api.get('/articles', { params: category ? { category } : {} }),
  getBySlug: (slug) => api.get(`/articles/${slug}`),
};

// Weekly teardown newsletter — public, no auth. Double opt-in: the backend emails
// a confirmation link; the response never reveals whether the address existed.
export const newsletterAPI = {
  subscribe: (email, source) => api.post('/newsletter/subscribe', { email, source }),
};

export const attemptsAPI = {
  create: (data) => api.post('/attempts', data),
  getAll: (params) => api.get('/attempts', { params }),
  getById: (id) => api.get(`/attempts/${id}`),
  update: (id, data) => api.put(`/attempts/${id}`, data),
  submit: (id) => api.post(`/attempts/${id}/submit`),
  delete: (id) => api.delete(`/attempts/${id}`),
  updateDiagramContext: (id, diagramText) =>
    api.post(`/attempts/${id}/diagram-context`, { diagram_text: diagramText }),
  createShareCard: (id) => api.post(`/attempts/${id}/share`),
};

// Public shareable scorecards — no auth required.
export const shareAPI = {
  get: (shareId) => api.get(`/share/${shareId}/data`),
};

// Drills — short-form practice with instant feedback (free & unlimited).
export const drillsAPI = {
  getTypes: () => api.get('/drills/types'),
  start: (type) => api.post('/drills/start', type ? { type } : {}),
  submit: (drillId, answer) => api.post(`/drills/${drillId}/submit`, { answer }),
  history: (limit = 20) => api.get('/drills/history', { params: { limit } }),
  // Attach anonymous /try drills (by this browser's anon id) to the signed-in user.
  claim: (anonId) => api.post('/drills/claim', { anon_id: anonId }),
};

// Anonymous "try a drill" — no auth. anonId is persisted in localStorage (sdl_anon_id).
export const tryAPI = {
  start: ({ type, anonId, challengeId, from } = {}) =>
    api.post('/try/drills/start', {
      type: type || undefined,
      anon_id: anonId || undefined,
      challenge_id: challengeId || undefined,
      from: from || undefined,
    }),
  submit: (drillId, answer, anonId) =>
    api.post(`/try/drills/${drillId}/submit`, { answer, anon_id: anonId }),
};

// "Challenge a friend". create/submit use optional auth (anonId for anonymous owners).
export const challengesAPI = {
  create: (drillId, anonId) =>
    api.post('/challenges', { drill_id: drillId, anon_id: anonId || undefined }),
  get: (challengeId) => api.get(`/challenges/${challengeId}`),
  start: (challengeId) => api.post(`/challenges/${challengeId}/start`),
  submit: (challengeId, drillId, answer, anonId) =>
    api.post(`/challenges/${challengeId}/submit/${drillId}`, { answer, anon_id: anonId || undefined }),
};

// Growth: public free-mode flag + drill-first onboarding state.
export const growthAPI = {
  getConfig: () => api.get('/growth/config'),
  getOnboardingState: () => api.get('/growth/onboarding-state'),
};

// Daily Design — one shared 5-min problem per UTC day. get/leaderboard are public
// (the interceptor simply omits the Authorization header when logged out).
export const dailyAPI = {
  get: () => api.get('/daily'),
  start: () => api.post('/daily/start'),
  submit: (drillId, answer) => api.post(`/daily/${drillId}/submit`, { answer }),
  leaderboard: (date, limit) =>
    api.get('/daily/leaderboard', { params: { ...(date ? { date } : {}), ...(limit ? { limit } : {}) } }),
  history: () => api.get('/daily/history'),
};

export const dashboardAPI = {
  getAnalytics: () => api.get('/dashboard/analytics'),
  getSkillProfile: () => api.get('/dashboard/skill-profile'),
  getStreakLeaderboard: (limit = 10) => api.get('/dashboard/streak-leaderboard', { params: { limit } }),
};

export const practiceAPI = {
  getRecommendations: () => api.get('/practice/recommendations'),
};

export const subscriptionAPI = {
  getStatus: () => api.get('/subscription/status').then((r) => {
    noteGrowthFreeMode(r.data?.growth_free_mode); // keeps the global free-mode flag fresh
    return r;
  }),
  getPlans: () => api.get('/subscription/plans'),
  cancel: (reason) => api.post('/subscription/cancel', { reason }),
  cancelReason: (reason) => api.post('/subscription/cancel-reason', { reason }),
  pause: (months) => api.post('/subscription/pause', { months }),
  resume: () => api.post('/subscription/resume'),
};

export const accountAPI = {
  snooze: () => api.post('/account/snooze'),
};


export const feedbackAPI = {
  submit: (data) => api.post('/feedback', data),
};

export const profileAPI = {
  getPublic: (username) => api.get(`/users/${username}/profile`),
};

export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markAllRead: () => api.put('/notifications/read-all'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
};

export const learnAPI = {
  getModules: (params) => api.get('/learn/modules', { params }),
  getModule: (id) => api.get(`/learn/modules/${id}`),
  markProgress: (id, sectionId) => api.post(`/learn/modules/${id}/progress`, { section_id: sectionId }),
  getProgress: () => api.get('/learn/progress'),
};

export const communityAPI = {
  getQuestions: () => api.get('/community/questions'),
  getPosts: (params) => api.get('/community/posts', { params }),
  createPost: (data) => api.post('/community/posts', data),
  getPost: (id) => api.get(`/community/posts/${id}`),
  toggleUpvote: (id) => api.post(`/community/posts/${id}/upvote`),
  addComment: (id, data) => api.post(`/community/posts/${id}/comments`, data),
  deletePost: (id) => api.delete(`/community/posts/${id}`),
  deleteComment: (postId, cid) => api.delete(`/community/posts/${postId}/comments/${cid}`),
};

export const quizAPI = {
  start: (params) => api.get('/quiz/start', { params }),
  submit: (id, data) => api.post(`/quiz/${id}/submit`, data),
  getHistory: () => api.get('/quiz/history'),
  getLeaderboard: (params) => api.get('/quiz/leaderboard', { params }),
};

export const incidentAPI = {
  getScenarios: (params) => api.get('/incident-scenarios', { params }),
  getScenario: (id) => api.get(`/incident-scenarios/${id}`),
  create: (data) => api.post('/incident-attempts', data),
  update: (id, data) => api.put(`/incident-attempts/${id}`, data),
};

export const getIncidentScenarios = (params) => api.get('/incident-scenarios', { params });
export const createIncidentAttempt = (data) => api.post('/incident-attempts', data);

export const incidentGameAPI = {
  submitScore:    (data)   => api.post('/incident-game/scores', data),
  getLeaderboard: (params) => api.get('/incident-game/leaderboard', { params }),
};

export const retentionAPI = {
  getStats: () => api.get('/admin/retention/stats'),
  getSequences: () => api.get('/admin/retention/sequences'),
  updateSequence: (id, data) => api.put(`/admin/retention/sequences/${id}`, data),
  getTemplates: (params) => api.get('/admin/retention/templates', { params }),
  getTemplate: (id) => api.get(`/admin/retention/templates/${id}`),
  updateTemplate: (id, data) => api.put(`/admin/retention/templates/${id}`, data),
  testSend: (id, data) => api.post(`/admin/retention/templates/${id}/test`, data),
  getStreakConfig: () => api.get('/admin/retention/streak-config'),
  updateStreakConfig: (data) => api.put('/admin/retention/streak-config', data),
  getLogs: (params) => api.get('/admin/retention/logs', { params }),
  getUnsubscribes: (params) => api.get('/admin/retention/unsubscribes', { params }),
  resubscribe: (userId) => api.post(`/admin/retention/resubscribe/${userId}`),
  createPromoCode: (data) => api.post('/admin/retention/promo-codes', data),
  getPromoCodes: () => api.get('/admin/retention/promo-codes'),
};

export const paymentAPI = {
  createOrder: (data) => api.post('/payment/create-order', data),
  verify: (data) => api.post('/payment/verify', data),
};

export default api;
